import { t } from 'i18next';
import { StudyLevel } from '../../Types/interfaces';
import type {
  DepartmentPostDto,
  SessionPostDto,
  StudyLinePostDto,
  TeachingLanguage,
  University,
  UniversitySetupPostDto,
} from '../../Types/interfaces';
import type { StudyLineType } from '../../Types/interfaces';

const BACHELOR_NUMBER_OF_YEARS = 3;
const MASTER_NUMBER_OF_YEARS = 2;

// How many years a study line of the given level usually lasts
export const getDefaultNumberOfYears = (studyLevel: StudyLevel): number =>
  studyLevel === StudyLevel.Master
    ? MASTER_NUMBER_OF_YEARS
    : BACHELOR_NUMBER_OF_YEARS;

// Builds the departments with their study lines and sessions from the values of the setup forms
export const buildDepartmentsSetup = (
  uni: Pick<University, 'departments' | 'studyLines' | 'sessions'>
): Array<DepartmentPostDto> => {
  if (uni.departments === undefined) {
    throw new Error(t('uniForm.errors.missingDepartments'));
  }

  if (uni.studyLines === undefined) {
    throw new Error(t('uniForm.errors.missingStudyLines'));
  }

  if (uni.sessions === undefined) {
    throw new Error(t('uniForm.errors.missingSessions'));
  }

  // The sessions form lists the study lines of all departments one after the other,
  // so the sessions of a study line are found by its position in that list
  let sessionListIndex = 0;

  return uni.departments.map(
    (
      dep: {
        departmentName: string;
        deans: Array<string>;
        projectCoordinators?: Array<string>;
      },
      departmentIndex: number
    ) => {
      const studyLines: Array<StudyLinePostDto> = uni.studyLines[
        departmentIndex
      ].map(
        (sl: {
          studyLineName: string;
          studyLineLevel: StudyLevel;
          studyLineType: StudyLineType;
          teachingLanguage: TeachingLanguage;
          numberOfYears?: number;
          coordinators: Array<string>;
        }) => {
          const sessions: Array<SessionPostDto> = (
            uni.sessions[sessionListIndex++] ?? []
          ).map(
            (s: {
              description: string;
              yearsOfStudy: number;
              courseCredit: string;
              sessionHours: number;
            }) => {
              return {
                description: s.description,
                yearOfStudy: s.yearsOfStudy,
                courseCredit: String(s.courseCredit),
                hours: s.sessionHours,
              };
            }
          );
          return {
            name: sl.studyLineName,
            studyLevel: sl.studyLineLevel,
            sessions: sessions,
            studyLineType: sl.studyLineType,
            teachingLanguage: sl.teachingLanguage,
            // the form requires it, so it is set by now
            numberOfYears: sl.numberOfYears ?? 0,
            universityCoordinatorsEmails: sl.coordinators,
          };
        }
      );
      return {
        name: dep.departmentName,
        studyLines: studyLines,
        deansEmails: dep.deans || [],
        universityProjectCoordinatorEmails: dep.projectCoordinators || [],
      };
    }
  );
};

// The values of the setup forms from a saved setup, so the setup of the next study year starts from the previous one.
// Also returns the names of the departments and study lines, which the study line and session forms are built from
export const toSetupFormValues = (
  setup: UniversitySetupPostDto
): {
  values: Pick<University, 'departments' | 'studyLines' | 'sessions'>;
  departmentNames: Array<string>;
  studyLineNames: Array<Array<string>>;
} => {
  const departments = setup.departmentsSetup;

  return {
    values: {
      departments: departments.map((department) => ({
        departmentName: department.name,
        deans: department.deansEmails,
        // a study year set up before this list existed has no project coordinators
        projectCoordinators:
          department.universityProjectCoordinatorEmails ?? [],
      })),
      studyLines: departments.map((department) =>
        department.studyLines.map((studyLine) => ({
          studyLineName: studyLine.name,
          studyLineLevel: studyLine.studyLevel,
          studyLineType: studyLine.studyLineType,
          teachingLanguage: studyLine.teachingLanguage,
          // study lines saved before the number of years existed have 0,
          // they get the usual number of years of their level
          numberOfYears:
            studyLine.numberOfYears ||
            getDefaultNumberOfYears(studyLine.studyLevel),
          coordinators: studyLine.universityCoordinatorsEmails,
        }))
      ),
      // The sessions form lists the study lines of all departments one after the other
      sessions: departments.flatMap((department) =>
        department.studyLines.map((studyLine) =>
          studyLine.sessions.map((session) => ({
            description: session.description,
            yearsOfStudy: session.yearOfStudy,
            courseCredit: session.courseCredit,
            sessionHours: session.hours,
          }))
        )
      ),
    },
    departmentNames: departments.map((department) => department.name),
    studyLineNames: departments.map((department) =>
      department.studyLines.map((studyLine) => studyLine.name)
    ),
  };
};
